import React, { useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { AudioLines, Loader2, Check, Phone, Users, MapPin, UserPlus } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { marked } from 'marked';

const QUILL_MODULES = {
  toolbar: [[{ header: [2, 3, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }], ['clean']],
};

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.innerText || '').trim();
};

const TYPE_OPTIONS = [
  { value: 'Call', label: 'Call', Icon: Phone },
  { value: 'Meeting', label: 'Meeting', Icon: Users },
];
const CONTACT_OPTIONS = [
  { value: 'owner', label: 'Owner / Shareholder', active: 'border-emerald-500 bg-emerald-500/15 text-emerald-300' },
  { value: 'expert', label: 'Expert', active: 'border-violet-500 bg-violet-500/15 text-violet-300' },
  { value: 'internal', label: 'Internal (team)', active: 'border-border bg-secondary text-foreground' },
  { value: 'other', label: 'Other external', active: 'border-blue-500 bg-blue-500/15 text-blue-300' },
];

export function AddMeetingDialog({ projectId, open, onOpenChange, onSaved, event = null }) {
  const [type, setType] = useState('Call');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [contactType, setContactType] = useState('owner');
  const [isOnsite, setIsOnsite] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPlaud, setShowPlaud] = useState(false);
  const [recs, setRecs] = useState([]);
  const [inboxItems, setInboxItems] = useState([]);
  const [recsError, setRecsError] = useState('');
  const [recsLoading, setRecsLoading] = useState(false);
  const [fetchingId, setFetchingId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [extraName, setExtraName] = useState('');

  React.useEffect(() => {
    if (!open || !projectId) return;
    // Every contact linked to the deal (CRM + deal contacts card) — tick who was in the room (Talu, 08.09.2026)
    Promise.allSettled([api.getProjectContacts(projectId), api.getDealContacts(projectId)]).then(([crm, dc]) => {
      const seen = new Set();
      const out = [];
      const push = (name, role) => {
        const n = (name || '').trim();
        if (!n || seen.has(n.toLowerCase())) return;
        seen.add(n.toLowerCase()); out.push({ name: n, role: role || '' });
      };
      (crm.status === 'fulfilled' ? crm.value.data.contacts || [] : []).forEach(c => push([c.first_name, c.last_name].filter(Boolean).join(' ') || c.company, c.type));
      (dc.status === 'fulfilled' ? dc.value.data.contacts || [] : []).forEach(c => push(c.name, c.role === 'other' ? c.role_other : c.role));
      setContactOptions(out);
    });
  }, [open, projectId]);

  React.useEffect(() => {
    if (!open || !event) return;
    setType((event.type || '').toLowerCase() === 'meeting' ? 'Meeting' : 'Call');
    setTitle(event.title || '');
    setDate((event.date || event.created_at || new Date().toISOString()).slice(0, 10));
    setContactType(event.contact_type || 'owner');
    setIsOnsite(!!event.is_onsite);
    setParticipants(event.participants || []);
    let html = event.content_html || '';
    if (!html && event.content) {
      try { html = marked.parse(event.content, { breaks: true }); }
      catch { html = `<p>${(event.content || '').replace(/\n/g, '<br/>')}</p>`; }
    }
    setNotes(html);
  }, [open, event]);

  const reset = () => {
    setType('Call'); setTitle(''); setDate(new Date().toISOString().slice(0, 10));
    setContactType('owner'); setIsOnsite(false); setNotes(''); setShowPlaud(false); setRecs([]); setInboxItems([]); setRecsError('');
    setParticipants([]); setExtraName('');
  };

  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const toggleParticipant = (name) => setParticipants(p => p.includes(name) ? p.filter(x => x !== name) : [...p, name]);
  const addExtraName = () => {
    const n = extraName.trim();
    if (!n) return;
    if (!participants.includes(n)) setParticipants(p => [...p, n]);
    if (!contactOptions.some(c => c.name.toLowerCase() === n.toLowerCase())) setContactOptions(o => [...o, { name: n, role: '' }]);
    setExtraName('');
  };

  const togglePlaud = async () => {
    if (showPlaud) { setShowPlaud(false); return; }
    setShowPlaud(true);
    setRecsLoading(true);
    setRecsError('');
    // Already imported notes (Plaud inbox) are always available — the live recording list is a bonus
    // (Max, 14.09.2026: the live API failed on the deal page while Call Analytics worked).
    const [live, inbox] = await Promise.allSettled([api.getPlaudRecordings(14), api.webexPlaudItems()]);
    const items = inbox.status === 'fulfilled' ? (inbox.value.data.items || []) : [];
    setInboxItems(items);
    if (live.status === 'fulfilled') {
      const importedIds = new Set(items.map(i => i.recording_id).filter(Boolean));
      setRecs((live.value.data || []).filter(r => !importedIds.has(r.id)));
    } else {
      setRecs([]);
      const err = live.reason;
      setRecsError(err?.response?.status === 409
        ? 'Plaud ist nicht verbunden (Zahnrad auf der Team-Meeting-Seite) — bereits importierte Notizen sind trotzdem verfügbar.'
        : (err?.response?.data?.detail || 'Plaud-Aufnahmen konnten nicht geladen werden — bereits importierte Notizen sind trotzdem verfügbar.'));
    }
    setRecsLoading(false);
  };

  const importRec = async (rec) => {
    setFetchingId(rec.id);
    try {
      const r = await api.getPlaudRecordingNote(rec.id);
      const t = (r.data.text || '').trim();
      let html = '';
      try { html = marked.parse(t, { breaks: true }); } catch { html = `<p>${t.replace(/\n/g, '<br/>')}</p>`; }
      setNotes(prev => stripHtml(prev) ? `${prev}<p><br/></p>${html}` : html);
      if (!title.trim()) setTitle(rec.name || rec.subject || '');
      setShowPlaud(false);
      toast.success('Plaud-Notiz eingefügt — bitte prüfen und ggf. kürzen');
    } catch (err) { toast.error(err.response?.data?.detail || 'Import fehlgeschlagen'); }
    setFetchingId(null);
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    const plain = stripHtml(notes);
    setSaving(true);
    try {
      const payload = {
        type, title: title.trim(), date, contact_type: contactType,
        notes: plain, notes_html: plain ? notes : '',
        is_onsite: type === 'Meeting' ? isOnsite : false,
        participants,
      };
      if (event) {
        await api.updateEvent(projectId, event.id, payload);
        toast.success(`${type} aktualisiert`);
      } else {
        await api.addEvent(projectId, payload);
        toast.success(`${type} logged${contactType === 'owner' ? ' — counts as owner engagement' : ''}`);
      }
      reset();
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border rounded-sm max-w-xl max-h-[85vh] flex flex-col" data-testid="add-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground">
            {event ? 'Edit Call / Meeting' : 'Add Call / Meeting'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 mt-1 pr-1">
          <div className="flex gap-4">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Type</p>
              <div className="flex gap-1.5">
                {TYPE_OPTIONS.map(({ value, label, Icon }) => (
                  <button key={value} onClick={() => setType(value)} data-testid={`meeting-type-${value.toLowerCase()}`}
                    className={`flex items-center gap-1.5 text-xs border rounded-sm px-3 h-8 transition-colors ${type === value ? 'border-violet-500 bg-violet-500/15 text-violet-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Date</p>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="meeting-date-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs w-40" />
            </div>
          </div>

          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Title</p>
            <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="meeting-title-input"
              placeholder="e.g. Intro call with owner, plant visit debrief..."
              className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
          </div>

          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">With whom?</p>
            <div className="flex gap-1.5 flex-wrap">
              {CONTACT_OPTIONS.map(({ value, label, active }) => (
                <button key={value} onClick={() => setContactType(value)} data-testid={`contact-type-${value}`}
                  className={`text-xs border rounded-sm px-3 h-8 transition-colors ${contactType === value ? active : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Only "Owner / Shareholder" counts for owner engagement and stage advancement — expert calls don't move the stage.
            </p>
          </div>

          <div data-testid="meeting-participants">
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Teilnehmer (Deal-Kontakte antippen)</p>
            <div className="flex gap-1.5 flex-wrap">
              {contactOptions.map(c => {
                const on = participants.includes(c.name);
                return (
                  <button key={c.name} type="button" onClick={() => toggleParticipant(c.name)} data-testid={`participant-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`text-xs border rounded-sm px-2.5 h-7 transition-colors inline-flex items-center gap-1.5 ${on ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    {on && <Check className="w-3 h-3" />}{c.name}{c.role ? <span className="text-[9px] opacity-70">· {c.role}</span> : null}
                  </button>
                );
              })}
              <div className="inline-flex items-center gap-1">
                <Input value={extraName} onChange={e => setExtraName(e.target.value)} data-testid="participant-extra-input"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtraName(); } }}
                  placeholder="weitere Person…" className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs w-40" />
                <Button type="button" variant="outline" size="sm" onClick={addExtraName} disabled={!extraName.trim()} data-testid="participant-extra-add"
                  className="h-7 px-2 text-[11px] rounded-sm"><UserPlus className="w-3 h-3" /></Button>
              </div>
            </div>
            {contactOptions.length === 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">Noch keine Kontakte am Deal — Namen oben eintragen oder Kontakte in der Kontakte-Karte anlegen.</p>
            )}
          </div>

          {type === 'Meeting' && (
            <button onClick={() => setIsOnsite(!isOnsite)} data-testid="meeting-onsite-toggle"
              className={`flex items-center gap-2 text-xs border rounded-sm px-3 h-8 transition-colors ${isOnsite ? 'border-amber-500 bg-amber-500/15 text-amber-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
              <MapPin className="w-3.5 h-3.5" />
              {isOnsite ? 'On-site visit at the company ✓' : 'Was this an on-site visit at the company?'}
            </button>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Notes</p>
              <Button variant="outline" size="sm" onClick={togglePlaud} data-testid="plaud-import-toggle"
                className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-[11px] h-6 px-2">
                <AudioLines className="w-3 h-3 mr-1" /> Import from Plaud
              </Button>
            </div>
            {showPlaud && (
              <div className="border border-violet-500/30 bg-violet-500/5 rounded-sm p-2 mb-2 max-h-56 overflow-y-auto" data-testid="plaud-rec-list">
                {recsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                  </div>
                ) : (
                  <>
                    {recsError && <p className="text-[11px] text-amber-300 px-1 pb-1" data-testid="plaud-rec-error">{recsError}</p>}
                    {inboxItems.length > 0 && (
                      <p className="px-1 pt-1 pb-0.5 text-[9px] uppercase tracking-wider text-violet-300/80">Bereits importierte Plaud-Notizen</p>
                    )}
                    {inboxItems.map(it => (
                      <button key={it.id} onClick={() => importRec({ id: it.id, name: it.subject })} disabled={!!fetchingId}
                        data-testid={`plaud-inbox-item-${it.id}`}
                        className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-sm hover:bg-violet-500/10 transition-colors disabled:opacity-50">
                        {fetchingId === it.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400 shrink-0" />
                          : <AudioLines className="w-3.5 h-3.5 text-violet-400 shrink-0" />}
                        <span className="text-xs text-foreground truncate flex-1">{it.subject}</span>
                        <span className="text-[10px] text-muted-foreground font-mono shrink-0">{(it.received_at || '').slice(0, 10)}</span>
                      </button>
                    ))}
                    {recs.length > 0 && (
                      <p className="px-1 pt-2 pb-0.5 text-[9px] uppercase tracking-wider text-violet-300/80">Neue Aufnahmen (letzte 14 Tage)</p>
                    )}
                    {recs.map(rec => (
                      <button key={rec.id} onClick={() => importRec(rec)} disabled={!!fetchingId}
                        data-testid={`plaud-rec-item-${rec.id}`}
                        className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-sm hover:bg-violet-500/10 transition-colors disabled:opacity-50">
                        {fetchingId === rec.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400 shrink-0" />
                          : <AudioLines className="w-3.5 h-3.5 text-violet-400 shrink-0" />}
                        <span className="text-xs text-foreground truncate flex-1">{rec.name}</span>
                        <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                          {(rec.created_at || '').slice(0, 10)}{rec.duration_min ? ` · ${rec.duration_min}min` : ''}
                        </span>
                      </button>
                    ))}
                    {recs.length === 0 && inboxItems.length === 0 && !recsError && (
                      <p className="text-xs text-muted-foreground italic px-1 py-2">Keine Plaud-Aufnahmen in den letzten 14 Tagen.</p>
                    )}
                  </>
                )}
              </div>
            )}
            <div data-testid="meeting-notes-input">
              <ReactQuill theme="snow" value={notes} onChange={setNotes} modules={QUILL_MODULES}
                placeholder="Type notes manually, paste from Word, or import the Plaud summary above..." />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Pasted rich text (Word, Outlook, Notion — bold, bullets, headings) keeps its formatting.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" onClick={() => handleClose(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !title.trim()} data-testid="add-meeting-save-btn"
            className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-4">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
